import { useState, useEffect } from "react";

// Module-level components give React a stable identity and prevent focus loss
// on re-renders (same lesson as LandingPage modals).

export default function ResetPassword({ supabase }) {
  const [session,  setSession]  = useState(null);
  const [authDone, setAuthDone] = useState(false);

  useEffect(() => {
    // PASSWORD_RECOVERY fires when Supabase exchanges the recovery token from the hash.
    // We also listen for SIGNED_IN in case the token was already exchanged.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setSession(sess);
      }
      setAuthDone(true);
    });
    // Handle the case where getSession processes the hash before the listener fires
    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      setSession(sess);
      setAuthDone(true);
    });
    return () => subscription.unsubscribe();
  }, [supabase]);

  if (!authDone) return <Splash />;
  if (!session)  return <ExpiredLink />;
  return <PasswordForm supabase={supabase} />;
}

// ── Password form ─────────────────────────────────────────────────────────────
function PasswordForm({ supabase }) {
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const [done,     setDone]     = useState(false);
  const [error,    setError]    = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8)  { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm)  { setError("Passwords do not match."); return; }

    setSaving(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw new Error(updateErr.message);
      setDone(true);
      // Reload without hash so the app starts fresh in dashboard mode
      setTimeout(() => { window.location.href = "/app"; }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const field = {
    width: "100%", padding: "11px 14px", fontSize: 15,
    border: "1px solid #D4CFC7", borderRadius: 8, outline: "none",
    fontFamily: "'Manrope', sans-serif", background: "#fff",
    color: "#2B2B2B", boxSizing: "border-box",
  };
  const lbl = {
    fontSize: 12, fontWeight: 600, color: "#2B2B2B",
    fontFamily: "'Manrope', sans-serif", marginBottom: 5, display: "block",
  };

  return (
    <Shell>
      {done ? (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ fontSize: 44, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 26, color: "#163C2F", margin: "0 0 10px" }}>
            Password updated!
          </h2>
          <p style={{ fontSize: 14, color: "#9B9690", lineHeight: 1.6, margin: 0 }}>
            Taking you to the dashboard…
          </p>
        </div>
      ) : (
        <>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 28, color: "#2B2B2B", margin: "0 0 8px" }}>
            Set a new password
          </h1>
          <p style={{ fontSize: 13, color: "#9B9690", margin: "0 0 28px", lineHeight: 1.6 }}>
            Choose a strong password for your Leasly account.
          </p>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={lbl}>New Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={field}
                placeholder="At least 8 characters"
                autoFocus
                required
              />
            </div>

            <div>
              <label style={lbl}>Confirm New Password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                style={field}
                placeholder="Re-enter your new password"
                required
              />
            </div>

            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626" }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              style={{
                background: saving ? "#9B9690" : "#163C2F",
                color: "#F5F1EA", border: "none", borderRadius: 999,
                padding: "14px 0", fontFamily: "'Manrope', sans-serif",
                fontSize: 13, fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                textTransform: "uppercase", letterSpacing: "0.08em",
                transition: "background 0.15s", width: "100%",
              }}
            >
              {saving ? "Saving…" : "Update Password"}
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}

// ── Splash while the recovery token is being exchanged ────────────────────────
function Splash() {
  return (
    <Shell>
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 32, height: 32, border: "3px solid #E8E4DC", borderTop: "3px solid #163C2F", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 20px" }} />
        <p style={{ fontSize: 14, color: "#9B9690", margin: 0 }}>Verifying your reset link…</p>
      </div>
    </Shell>
  );
}

// ── Expired / invalid link state ──────────────────────────────────────────────
function ExpiredLink() {
  return (
    <Shell>
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 24, color: "#2B2B2B", margin: "0 0 10px" }}>
          Link expired or invalid
        </h2>
        <p style={{ fontSize: 14, color: "#9B9690", lineHeight: 1.65, margin: "0 0 24px" }}>
          This reset link has already been used or has expired. Return to the sign-in page and request a new one.
        </p>
        <a href="/app?signin" style={{ display: "inline-block", background: "#163C2F", color: "#F5F1EA", borderRadius: 999, padding: "12px 28px", fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Back to Sign In
        </a>
      </div>
    </Shell>
  );
}

// ── Shared page shell ─────────────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#F5F1EA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'Manrope', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Serif+Display:ital@0;1&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
        <img src="/Leasly-logofinal.png" alt="Leasly" width="34" height="34" style={{ borderRadius: 7, objectFit: "contain" }} />
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: "#163C2F", letterSpacing: "0.01em" }}>Leasly</span>
      </div>

      <div style={{ background: "#FFFFFF", border: "1px solid #E8E4DC", borderRadius: 16, padding: "36px 40px", width: "100%", maxWidth: 440, boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>
        {children}
      </div>
    </div>
  );
}
